import { Skeleton, styled, SxProps, Theme, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useRiskOverview } from "../../hooks/useRiskOverview";
import { BlockedByList } from "./BlockedByList";
import { RuleScoreList } from "./RuleScoreList";

interface RiskOverviewProps {
  containerSx?: SxProps<Theme>;
}

export function RiskOverview({ containerSx }: RiskOverviewProps) {
  const { t } = useTranslation();
  const { blockList, riskScore, overallRisk, isSameCustomer } =
    useRiskOverview();

  if (!blockList?.ruleResults?.length && !riskScore?.matchedRules?.length) {
    return null;
  }

  const overallRiskScore = overallRisk.score ?? 0;
  const overallRiskThreshold = overallRisk.threshold ?? 0;

  return (
    <div>
      <Container sx={containerSx}>
        <Typography variant="h6">{t("Risk overview")}</Typography>
        {riskScore && riskScore.matchedRules?.length > 0 && (
          <Typography
            variant="h6"
            sx={{
              color:
                overallRiskScore > overallRiskThreshold
                  ? "failure.main"
                  : "text.medium",
            }}
          >
            {overallRiskScore}
          </Typography>
        )}
      </Container>
      {riskScore && riskScore.matchedRules.length > 0 && (
        <Wrapper>
          <RuleScoreList isSameCustomer={isSameCustomer} />
        </Wrapper>
      )}
      {blockList && blockList.ruleResults.length > 0 && (
        <Wrapper>
          <BlockedByList isSameCustomer={isSameCustomer} />
        </Wrapper>
      )}
    </div>
  );
}

export function RiskOverviewLoadingState() {
  return (
    <div>
      <Container>
        <Skeleton sx={{ width: 100, height: 14 }} />
        <Skeleton sx={{ width: 100, height: 14 }} />
      </Container>
      <SkeletonColumnWrapper>
        <SkeletonRowWrapper>
          <Skeleton sx={{ width: 100, height: 14 }} />
          <Skeleton sx={{ width: 100, height: 14 }} />
        </SkeletonRowWrapper>
        <div>
          <Skeleton sx={{ width: "100%", height: 14, mb: 1 }} />
          <Skeleton sx={{ width: "100%", height: 14 }} />
        </div>
      </SkeletonColumnWrapper>
      <Wrapper>
        <Skeleton sx={{ width: 100, height: 14, mb: 1 }} />
        <Skeleton sx={{ width: "100%", height: 14 }} />
      </Wrapper>
    </div>
  );
}

const Container = styled("div")(({ theme }) => ({
  justifyContent: "space-between",
  display: "flex",
  padding: theme.spacing(1, 0),
}));

const Wrapper = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const SkeletonColumnWrapper = styled("div")(({ theme }) => ({
  flexDirection: "column",
  display: "flex",
  marginTop: theme.spacing(2),
}));

const SkeletonRowWrapper = styled("div")(({ theme }) => ({
  justifyContent: "space-between",
  display: "flex",
  marginBottom: theme.spacing(1),
}));
